"use client";

import axios from "axios";
import { useEffect, useState } from "react";
import { detectBrand } from ".";

// The resolved tenant is written to every sink a Shesha client reads, so ALL request paths carry
// Abp.TenantId regardless of which client makes the call:
//  - localStorage["TENANT"]: @shesha-io/reactjs's data/CRUD/form-submission client reads this to set
//    the Abp.TenantId header (verified in the shipped bundle). This is the path the registration
//    form uses, so it is the load-bearing one.
//  - Abp.TenantId cookie: read by the backend's cookie tenant resolver on same-origin requests.
//  - the returned value also feeds app-provider's buildHttpRequestHeaders for the auth client.
const TENANT_LOCALSTORAGE = "TENANT";
const TENANT_COOKIE = "Abp.TenantId";
const STATE_AVAILABLE = 1;

function persistTenant(id: number): void {
  window.localStorage.setItem(TENANT_LOCALSTORAGE, String(id));
  document.cookie = `${TENANT_COOKIE}=${id}; path=/; SameSite=Lax`;
}

function clearTenant(): void {
  window.localStorage.removeItem(TENANT_LOCALSTORAGE);
  document.cookie = `${TENANT_COOKIE}=; path=/; Max-Age=0; SameSite=Lax`;
}

function readCachedTenant(): number | null {
  if (typeof window === "undefined") return null;
  const cached = window.localStorage.getItem(TENANT_LOCALSTORAGE);
  if (!cached) return null;
  const parsed = parseInt(cached, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Resolves the current brand (pbf/pcf, from the hostname) to its ABP tenant id via the anonymous
 * Account/IsTenantAvailable endpoint, and persists it everywhere the Shesha clients read the tenant
 * so every request carries Abp.TenantId. Tenant ids are environment-specific, so we always resolve
 * by tenancy name. Returns the id, or null for the 'default' brand / host. Seeds synchronously from
 * the cache so the value is present on first render when resolved previously.
 */
export function useTenantResolver(backendUrl: string): number | null {
  const [tenantId, setTenantId] = useState<number | null>(readCachedTenant);

  useEffect(() => {
    const brand = detectBrand();

    if (brand.key === "default") {
      clearTenant();
      setTenantId(null);
      return;
    }

    let cancelled = false;
    const base = backendUrl.replace(/\/+$/, "");

    (async () => {
      try {
        const response = await axios.post(
          `${base}/api/services/MembershipManagement/Account/IsTenantAvailable`,
          { tenancyName: brand.key }
        );
        const result = response.data?.result;
        const resolved =
          result?.state === STATE_AVAILABLE && typeof result?.tenantId === "number"
            ? (result.tenantId as number)
            : null;

        if (cancelled) return;

        if (resolved != null) {
          persistTenant(resolved);
          setTenantId(resolved);
        } else {
          clearTenant();
          setTenantId(null);
        }
      } catch {
        // Network/parse failure — keep whatever is cached; do not block the app.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [backendUrl]);

  return tenantId;
}
